import React, { useState, useEffect, useRef } from "react";
import AuthLayout from "../../layout/AuthLayout";
import {
  Backdrop,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CustomInput } from "../../components/Common/CustomInputs";
import {
  LockOutlined,
  CheckCircleOutline,
  ContentCopyOutlined,
  EmailOutlined,
} from "@mui/icons-material";
import { CustomBtn, SecondaryBtn } from "../../components/Common/CustomBtn";
import axiosInstance from "../../utils/config/config";
import {
  ErrorSnackbar,
  ConfirmationSnackbar,
} from "../../components/Common/CustomSnackbar";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getUserById } from "../../redux/actions/user.dispatcher";

const RESEND_COOLDOWN_SECONDS = 60;

const TwoFactorSetup = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { userData } = useSelector((state) => state.user);

  // Step 1: Email code verification
  // Step 2: Show recovery codes
  const [step, setStep] = useState(1);
  const [codeSent, setCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  const [errorState, setErrorState] = useState({
    error: false,
    errorMsg: "",
  });
  const [successState, setSuccessState] = useState({
    success: false,
    successMsg: "",
  });

  // Start cooldown timer
  const startCooldown = () => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const sendCode = async () => {
    try {
      setLoading(true);
      setErrorState({ error: false, errorMsg: "" });

      await axiosInstance.post("/user/2fa/send-code", {
        userId: userData?.id,
      });

      setCodeSent(true);
      startCooldown();
      setSuccessState({
        success: true,
        successMsg: "Verification code sent to your email!",
      });
    } catch (error) {
      if (
        error.response?.status === 400 &&
        error.response?.data?.message?.includes("already enabled")
      ) {
        navigate("/home");
        return;
      }
      if (
        error.response?.status === 400 &&
        error.response?.data?.message?.includes("exempt")
      ) {
        navigate("/home");
        return;
      }
      if (error.response?.status === 429) {
        setErrorState({
          error: true,
          errorMsg: "Too many requests. Please wait a minute before trying again.",
        });
        return;
      }
      setErrorState({
        error: true,
        errorMsg:
          error.response?.data?.message || "Failed to send verification code.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter code input to digits only, max 6
  const handleCodeChange = (val) => {
    const filtered = val.replace(/\D/g, "").slice(0, 6);
    setVerificationCode(filtered);
  };

  const handleVerify = async () => {
    setErrorState({ error: false, errorMsg: "" });

    if (verificationCode.length !== 6) {
      setErrorState({
        error: true,
        errorMsg: "Please enter a 6-digit verification code.",
      });
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post("/user/2fa/verify-setup", {
        token: verificationCode,
      });

      setRecoveryCodes(response.data.recoveryCodes);
      setStep(2);

      // Refresh user data so twoFactorEnabled is updated in Redux
      dispatch(getUserById());
    } catch (error) {
      setErrorState({
        error: true,
        errorMsg:
          error.response?.data?.message || "Invalid code. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCodes = () => {
    const codesText = recoveryCodes.join("\n");
    navigator.clipboard.writeText(codesText).then(() => {
      setSuccessState({
        success: true,
        successMsg: "Recovery codes copied to clipboard!",
      });
    });
  };

  const handleContinue = () => {
    navigate("/home");
  };

  // Mask the email: show first 3 chars + ***@domain
  const maskedEmail = userData?.email
    ? `${userData.email.slice(0, 3)}***@${userData.email.split("@")[1]}`
    : "your email";

  return (
    <AuthLayout>
      <Box
        sx={{ px: 4, width: "100%" }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && step === 1) {
            handleVerify();
          }
        }}
      >
        {step === 1 && (
          <>
            <Typography
              sx={{
                fontFamily: "'Gotham', sans-serif",
                fontSize: "36px",
                color: theme.palette.text.primary,
              }}
            >
              Set Up Two-Factor Authentication
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Quicksand', sans-serif",
                fontSize: "18px",
                color: theme.palette.text.primary,
                mt: 1,
              }}
            >
              {codeSent
                ? `We've sent a 6-digit verification code to ${maskedEmail}`
                : "We'll send a verification code to your email to get started."}
            </Typography>

            {codeSent && (
              <>
                <Box sx={{ my: 3, width: "100%" }}>
                  <CustomInput
                    placeholder="Enter 6-digit code"
                    value={verificationCode}
                    setValue={handleCodeChange}
                    Icon={LockOutlined}
                    width={"100%"}
                    color={theme.palette.text.primary}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: mediumScreen ? "column" : "row",
                    gap: 2,
                    alignItems: "flex-start",
                  }}
                >
                  <CustomBtn
                    text="Verify & Enable"
                    Icon={CheckCircleOutline}
                    width={mediumScreen ? "60%" : "40%"}
                    onClick={handleVerify}
                  />
                </Box>

                <Typography
                  onClick={resendCooldown === 0 ? sendCode : undefined}
                  sx={{
                    fontFamily: "'Quicksand', sans-serif",
                    fontSize: "16px",
                    color: theme.palette.text.primary,
                    textDecorationLine:
                      resendCooldown === 0 ? "underline" : "none",
                    cursor: resendCooldown === 0 ? "pointer" : "default",
                    opacity: resendCooldown === 0 ? 1 : 0.5,
                    mt: 2,
                  }}
                >
                  {resendCooldown > 0
                    ? `Resend code in ${resendCooldown}s`
                    : "Resend code"}
                </Typography>
              </>
            )}

            {!codeSent && !loading && (
              <Box sx={{ mt: 3 }}>
                <CustomBtn
                  text="Send Code"
                  Icon={EmailOutlined}
                  width={mediumScreen ? "60%" : "40%"}
                  onClick={sendCode}
                />
              </Box>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <Typography
              sx={{
                fontFamily: "'Gotham', sans-serif",
                fontSize: "36px",
                color: theme.palette.text.primary,
              }}
            >
              Save Your Recovery Codes
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Quicksand', sans-serif",
                fontSize: "18px",
                color: theme.palette.text.primary,
                mt: 1,
              }}
            >
              Save these codes in a safe place. Each code can only be used once.
              If you lose access to your email, these are the only way to regain
              access to your account.
            </Typography>

            <Box
              sx={{
                my: 3,
                p: 3,
                backgroundColor: "rgba(0,0,0,0.04)",
                borderRadius: 2,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1.5,
                }}
              >
                {recoveryCodes.map((code, index) => (
                  <Typography
                    key={index}
                    sx={{
                      fontFamily: "'Courier New', monospace",
                      fontSize: "16px",
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      p: 1,
                      backgroundColor: "#fff",
                      borderRadius: 1,
                      textAlign: "center",
                      letterSpacing: "1px",
                    }}
                  >
                    {code}
                  </Typography>
                ))}
              </Box>

              <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                <SecondaryBtn
                  text="Copy All Codes"
                  Icon={ContentCopyOutlined}
                  width={mediumScreen ? "60%" : "40%"}
                  onClick={handleCopyCodes}
                />
              </Box>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={savedConfirmed}
                  onChange={(e) => setSavedConfirmed(e.target.checked)}
                  sx={{
                    color: theme.palette.text.primary,
                    "&.Mui-checked": {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: "'Quicksand', sans-serif",
                    fontSize: "16px",
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                  }}
                >
                  I have saved these recovery codes in a safe place
                </Typography>
              }
              sx={{ mb: 2 }}
            />

            <CustomBtn
              text="Continue to Dashboard"
              Icon={CheckCircleOutline}
              width={mediumScreen ? "60%" : "50%"}
              onClick={handleContinue}
              disabled={!savedConfirmed}
            />
          </>
        )}
      </Box>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <ErrorSnackbar
        error={errorState.error}
        handleCloseError={() => setErrorState({ error: false, errorMsg: "" })}
        errorMsg={errorState.errorMsg}
      />
      <ConfirmationSnackbar
        snackbarState={successState.success}
        handleSnackbarClose={() =>
          setSuccessState({ success: false, successMsg: "" })
        }
        text={successState.successMsg}
      />
    </AuthLayout>
  );
};

export default TwoFactorSetup;
